"use client";

/**
 * MBR-16: entrar com código no e-mail, sem senha e sem sair da página.
 *
 * O pedido do código responde sempre igual, exista a conta ou não: a mensagem
 * de "enviamos" é neutra de propósito, para não revelar quem tem conta. O token
 * devolvido é o mesmo do login por senha; quem usa o componente decide o que
 * fazer com ele (o wizard carrega a sessão e segue para "Seus dados").
 */

import { useEffect, useRef, useState } from "react";
import { FiKey, FiMail } from "react-icons/fi";
import { Alert, Button, Input } from "../../../components/ui";
import {
  requestCustomerLoginCode,
  verifyCustomerLoginCode,
} from "../../../services/customerAuth";

const CODE_LENGTH = 6;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LOGIN_CODE_SENT_MESSAGE =
  "Se este e-mail tiver conta, enviamos um código de 6 dígitos. Ele vale 10 minutos.";
const INVALID_CODE_MESSAGE = "Código inválido ou vencido. Confira ou peça um novo.";
const TOO_MANY_ATTEMPTS_MESSAGE = "Muitas tentativas. Peça um novo código.";

interface BookingCodeLoginProps {
  initialEmail?: string;
  submitLabel?: string;
  onSuccess: (token: string) => Promise<void> | void;
  disabled?: boolean;
}

export function BookingCodeLogin({
  initialEmail = "",
  submitLabel = "Entrar",
  onSuccess,
  disabled = false,
}: BookingCodeLoginProps) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const inFlight = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const normalizedEmail = email.trim().toLowerCase();

  const handleRequestCode = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (inFlight.current || disabled) return;
    if (!EMAIL_RE.test(normalizedEmail)) {
      setEmailError("Informe um e-mail válido.");
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setEmailError(null);
    setCodeError(null);
    try {
      await requestCustomerLoginCode(normalizedEmail);
      if (!active.current) return;
      setWarning(null);
      setCode("");
      setStep("code");
    } catch (error: any) {
      if (!active.current) return;
      setEmailError(
        error?.code === "VALIDATION_ERROR"
          ? "Informe um e-mail válido."
          : error?.message || "Não foi possível enviar o código. Tente de novo."
      );
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };

  const handleVerifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current || disabled) return;
    if (code.length !== CODE_LENGTH) {
      setCodeError("Digite os 6 dígitos do código.");
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setCodeError(null);
    try {
      const { token } = await verifyCustomerLoginCode(normalizedEmail, code);
      if (!active.current) return;
      await onSuccess(token);
    } catch (error: any) {
      if (!active.current) return;
      if (error?.code === "TOO_MANY_ATTEMPTS" || error?.status === 429) {
        // O código foi invalidado: só um novo resolve.
        setCode("");
        setStep("email");
        setWarning(TOO_MANY_ATTEMPTS_MESSAGE);
      } else if (error?.code === "INVALID_OR_EXPIRED_CODE") {
        setCodeError(INVALID_CODE_MESSAGE);
      } else {
        setCodeError(error?.message || "Não foi possível validar o código.");
      }
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };

  if (step === "email") {
    return (
      <form className="space-y-3" onSubmit={handleRequestCode} noValidate>
        {warning && (
          <Alert tone="warning" mirrorToast={false}>
            {warning}
          </Alert>
        )}
        <Input
          label="E-mail da sua conta"
          icon={<FiMail />}
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          disabled={busy || disabled}
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailError(null);
          }}
          placeholder="seu@email.com"
          error={emailError || undefined}
        />
        <Button
          type="submit"
          variant="secondary"
          fullWidth
          isLoading={busy}
          disabled={disabled}
        >
          {warning ? "Enviar novo código" : "Enviar código"}
        </Button>
      </form>
    );
  }

  return (
    <form className="space-y-3" onSubmit={handleVerifyCode} noValidate>
      <Alert tone="info" mirrorToast={false}>
        {LOGIN_CODE_SENT_MESSAGE}
      </Alert>
      <Input
        label="Código de 6 dígitos"
        icon={<FiKey />}
        value={code}
        disabled={busy || disabled}
        onChange={(event) => {
          setCode(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
          setCodeError(null);
        }}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={CODE_LENGTH}
        placeholder="000000"
        error={codeError || undefined}
        helperText={normalizedEmail}
      />
      <Button
        type="submit"
        variant="primary"
        fullWidth
        isLoading={busy}
        disabled={disabled}
      >
        {submitLabel}
      </Button>
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={busy || disabled}
          onClick={() => {
            setStep("email");
            setCode("");
            setCodeError(null);
          }}
        >
          Trocar e-mail
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={busy || disabled}
          onClick={() => void handleRequestCode()}
        >
          Reenviar código
        </Button>
      </div>
    </form>
  );
}
